import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { useTranslation } from '../i18n/index';

export interface ZoomableImageProps {
  readonly src: string;
  readonly alt: string;
  readonly testId?: string;
}

const MIN = 1;
const MAX = 8;
const STEP = 1.25;

const clamp = (v: number): number => Math.min(MAX, Math.max(MIN, v));

/**
 * An image the reader can zoom and move about inside its own frame: the
 * buttons or the mouse wheel zoom, dragging moves a zoomed image, a double
 * click toggles between fitted and zoomed. Nothing leaves the frame.
 */
export function ZoomableImage({ src, alt, testId = 'zoomable-image' }: ZoomableImageProps): ReactNode {
  const { t } = useTranslation();
  const [scale, setScale] = useState(MIN);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  const reset = useCallback(() => { setScale(MIN); setOffset({ x: 0, y: 0 }); }, []);
  useEffect(() => { reset(); }, [src, reset]);

  const zoomTo = useCallback((next: number) => {
    const s = clamp(next);
    setScale(s);
    if (s === MIN) setOffset({ x: 0, y: 0 });
  }, []);

  // React attaches wheel listeners as passive, which cannot keep the page from
  // scrolling; the stage listens itself so the wheel zooms the photo instead.
  const stage = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setScale((s) => {
        const n = clamp(e.deltaY < 0 ? s * STEP : s / STEP);
        if (n === MIN) setOffset({ x: 0, y: 0 });
        return n;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (scale === MIN) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    setOffset({ x: d.ox + (e.clientX - d.x) / scale, y: d.oy + (e.clientY - d.y) / scale });
  };
  const endDrag = () => { drag.current = null; };

  return (
    <div className="tl-zoom" data-testid={testId}>
      <div className="tl-zoom__tools" role="toolbar" aria-label={t('viewer.zoom.tools')}>
        <button type="button" className="tl-zoom__btn" data-testid={`${testId}-out`} aria-label={t('viewer.zoom.out')}
                disabled={scale <= MIN} onClick={() => zoomTo(scale / STEP)}>−</button>
        <span className="tl-zoom__level" data-testid={`${testId}-level`}>{Math.round(scale * 100)}%</span>
        <button type="button" className="tl-zoom__btn" data-testid={`${testId}-in`} aria-label={t('viewer.zoom.in')}
                disabled={scale >= MAX} onClick={() => zoomTo(scale * STEP)}>+</button>
        <button type="button" className="tl-zoom__btn tl-zoom__btn--text" data-testid={`${testId}-reset`}
                disabled={scale === MIN} onClick={reset}>{t('viewer.zoom.fit')}</button>
      </div>
      <div
        ref={stage}
        className={`tl-zoom__stage${scale > MIN ? ' is-zoomed' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => (scale > MIN ? reset() : zoomTo(STEP * STEP))}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          data-testid={`${testId}-img`}
          style={{ transform: `scale(${scale}) translate(${offset.x}px, ${offset.y}px)` }}
        />
      </div>
    </div>
  );
}
